import { useCallback, useEffect } from 'react';

import { boundActionsFactory } from '@/packages/redux-state/store';
import { listingLayoutFiltersHeightSelector } from '@/packages/redux-state/store/slices/listingLayout';

import { useElementsRefContext } from '../context/ElementsRefContext';
import { useReduxState } from '../context/ReduxStoreContext';

import { useResizeObserver } from './useResizeObserver';

const useBoundActions = boundActionsFactory(reduxActions => ({
  setFiltersHeight: reduxActions.listingLayout.setFiltersHeight,
}));

export function useObserveFiltersHeightChange(): void {
  const { filtersRef, mapContainerRef } = useElementsRefContext();
  const actions = useBoundActions();
  const reduxState = useReduxState();

  const onFiltersWrapperSizeChange = useCallback(() => {
    const filters = filtersRef.current;

    if (!filters) {
      return;
    }

    const { height } = filters.getBoundingClientRect();

    const previousFiltersHeight = listingLayoutFiltersHeightSelector(reduxState.getState());

    if (height !== previousFiltersHeight) {
      actions.setFiltersHeight(height);
      mapContainerRef.current?.style.setProperty('--filters-height', `${height}px`);
    }
  }, [actions, filtersRef, mapContainerRef, reduxState]);

  useEffect(() => {
    onFiltersWrapperSizeChange();
  }, [onFiltersWrapperSizeChange]);

  const filtersObserver = useResizeObserver(onFiltersWrapperSizeChange);

  useEffect(() => {
    const filters = filtersRef.current;

    if (!filters || !filtersObserver) {
      return () => {};
    }

    filtersObserver.observe(filters);

    return () => {
      filtersObserver.unobserve(filters);
      filtersObserver.disconnect();
    };
  }, [filtersObserver, filtersRef]);
}
